import { useEffect, useState } from "react";
import { useNavigate, useParams, Link, useSearchParams } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { ArrowLeft, Save, History, ArrowRight } from "lucide-react";
import { CURRENCY_MAP, currencySymbol, formatMoney, formatDiffValue, humanizeField } from "@/lib/currency";

// Year/Month <-> total_months conversion
const calculateTotalMonths = (years, months) => (Number(years) || 0) * 12 + (Number(months) || 0);
const getYearsAndMonths = (total) => ({ years: Math.floor((total || 0) / 12), months: (total || 0) % 12 });

const schema = z.object({
  periodical_id: z.string().min(1, "Required"),
  plan_name: z.string().min(2, "Min 2 chars"),
  type: z.enum(["Offer Plan", "Fixed Plan"]),
  years: z.coerce.number().int().min(0).max(10),
  months: z.coerce.number().int().min(0).max(11),
  start_date: z.string().min(1, "Required"),
  end_date: z.string().min(1, "Required"),
  price: z.coerce.number().min(0, "Must be ≥ 0"),
  tax_percentage: z.coerce.number().min(0).max(100),
  grace_period_days: z.coerce.number().int().min(0).max(90),
  currency: z.string().min(3).max(4),
  early_bird_enabled: z.boolean().default(false),
  early_bird_days_threshold: z.coerce.number().int().min(1).max(180).default(10),
  early_bird_discount_percent: z.coerce.number().min(0).max(50).default(0),
}).refine((d) => calculateTotalMonths(d.years, d.months) >= 1, { message: "Duration must be at least 1 month", path: ["months"] })
  .refine((d) => new Date(d.end_date) > new Date(d.start_date), { message: "End date must be after start date", path: ["end_date"] })
  .refine((d) => !d.early_bird_enabled || d.early_bird_discount_percent > 0, { message: "Discount % required when early-bird is enabled", path: ["early_bird_discount_percent"] });

function Required() { return <span className="text-rose-600">*</span>; }

export default function SubscriptionPlanForm() {
  const { id } = useParams();
  const isEdit = !!id;
  const [params] = useSearchParams();
  const presetPer = params.get("periodical_id") || "";
  const navigate = useNavigate();
  const [orgs, setOrgs] = useState([]);
  const [pubs, setPubs] = useState([]);
  const [pers, setPers] = useState([]);
  const [orgId, setOrgId] = useState("");
  const [pubId, setPubId] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [history, setHistory] = useState([]);

  const today = new Date().toISOString().slice(0, 10);
  const { register, handleSubmit, watch, setValue, reset, formState: { errors } } = useForm({
    resolver: zodResolver(schema),
    defaultValues: {
      periodical_id: presetPer,
      type: "Fixed Plan",
      years: 1, months: 0,
      start_date: today, end_date: "",
      price: 0, tax_percentage: 5, grace_period_days: 0, currency: "INR",
      early_bird_enabled: false,
      early_bird_days_threshold: 10,
      early_bird_discount_percent: 0,
    },
  });

  const v = watch();
  const totalMonths = calculateTotalMonths(v.years, v.months);
  const finalPrice = (Number(v.price) || 0) * (1 + (Number(v.tax_percentage) || 0) / 100);

  useEffect(() => {
    (async () => {
      try { const { data } = await api.get("/organizations/active-list"); setOrgs(data); } catch { /* */ }
    })();
  }, []);

  // If presetPer is given, hydrate org & pub for context
  useEffect(() => {
    if (!presetPer || isEdit) return;
    (async () => {
      try {
        const { data } = await api.get(`/periodicals/${presetPer}`);
        const pub = await api.get(`/publication-houses/${data.publication_house_id}`);
        setOrgId(pub.data.organization_id);
        setPubId(pub.data.publication_house_id || data.publication_house_id);
        setValue("periodical_id", presetPer);
      } catch { /* */ }
    })();
  }, [presetPer, isEdit, setValue]);

  useEffect(() => {
    if (!orgId) { setPubs([]); return; }
    (async () => { try { const { data } = await api.get("/publication-houses/active-list", { params: { organization_id: orgId } }); setPubs(data); } catch { /* */ } })();
  }, [orgId]);

  useEffect(() => {
    if (!pubId) { setPers([]); return; }
    (async () => {
      try {
        const { data } = await api.get("/periodicals", { params: { publication_house_id: pubId, page: 1, page_size: 100 } });
        setPers(data.items.filter((x) => x.status === "active"));
      } catch { /* */ }
    })();
  }, [pubId]);

  useEffect(() => {
    if (!isEdit) return;
    (async () => {
      try {
        const { data } = await api.get(`/subscription-plans/${id}`);
        const per = await api.get(`/periodicals/${data.periodical_id}`);
        const pub = await api.get(`/publication-houses/${per.data.publication_house_id}`);
        setOrgId(pub.data.organization_id);
        setPubId(per.data.publication_house_id);
        const ym = getYearsAndMonths(data.total_months);
        reset({
          periodical_id: data.periodical_id,
          plan_name: data.plan_name,
          type: data.type,
          years: ym.years,
          months: ym.months,
          start_date: (data.start_date || "").slice(0, 10),
          end_date: (data.end_date || "").slice(0, 10),
          price: data.price ?? 0,
          tax_percentage: data.tax_percentage ?? 0,
          grace_period_days: data.grace_period_days ?? 0,
          currency: data.currency || "INR",
          early_bird_enabled: !!data.early_bird_enabled,
          early_bird_days_threshold: data.early_bird_days_threshold ?? 10,
          early_bird_discount_percent: data.early_bird_discount_percent ?? 0,
        });
        const h = await api.get(`/subscription-plans/${id}/history`);
        setHistory(h.data || []);
      } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
    })();
  }, [id, isEdit, reset]);

  const onSubmit = async (values) => {
    setSubmitting(true);
    try {
      const payload = {
        periodical_id: values.periodical_id,
        plan_name: values.plan_name,
        type: values.type,
        total_months: calculateTotalMonths(values.years, values.months),
        start_date: values.start_date,
        end_date: values.end_date,
        price: Number(values.price),
        tax_percentage: Number(values.tax_percentage),
        grace_period_days: Number(values.grace_period_days),
        currency: "INR",
        early_bird_enabled: !!values.early_bird_enabled,
        early_bird_days_threshold: Number(values.early_bird_days_threshold) || 10,
        early_bird_discount_percent: values.early_bird_enabled ? Number(values.early_bird_discount_percent) || 0 : 0,
      };
      if (isEdit) {
        await api.put(`/subscription-plans/${id}`, payload);
        toast.success("Plan updated");
      } else {
        await api.post("/subscription-plans", payload);
        toast.success("Plan created");
      }
      navigate("/subscription-plans");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setSubmitting(false); }
  };

  return (
    <div data-testid="plan-form-page" className="mx-auto max-w-5xl">
      <Button variant="ghost" size="sm" asChild className="mb-3 rounded-sm text-slate-600">
        <Link to="/subscription-plans"><ArrowLeft size={14} className="mr-1" />Back</Link>
      </Button>
      <h1 className="text-3xl font-semibold tracking-tight text-slate-900">{isEdit ? "Edit Subscription Plan" : "Create Subscription Plan"}</h1>

      <div className="mt-8 grid grid-cols-1 gap-6 lg:grid-cols-3">
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6 rounded-sm border border-slate-200 bg-white p-6 lg:col-span-2">
          <Section title="Hierarchy">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
              <Field label="Organization" required>
                <Select value={orgId} onValueChange={(v) => { setOrgId(v); setPubId(""); setValue("periodical_id", ""); }} disabled={isEdit}>
                  <SelectTrigger data-testid="plan-form-org" className="rounded-sm"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>{orgs.map((o) => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
              <Field label="Publication House" required>
                <Select value={pubId} onValueChange={(v) => { setPubId(v); setValue("periodical_id", ""); }} disabled={!orgId || isEdit}>
                  <SelectTrigger data-testid="plan-form-pub" className="rounded-sm"><SelectValue placeholder={orgId ? "Select" : "—"} /></SelectTrigger>
                  <SelectContent>{pubs.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
              <Field label="Periodical" required error={errors.periodical_id?.message}>
                <Select value={v.periodical_id || ""} onValueChange={(val) => setValue("periodical_id", val, { shouldValidate: true })} disabled={!pubId || isEdit}>
                  <SelectTrigger data-testid="plan-form-per" className="rounded-sm"><SelectValue placeholder={pubId ? "Select" : "—"} /></SelectTrigger>
                  <SelectContent>{pers.map((p) => <SelectItem key={p.id} value={p.id}>{p.title}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
            </div>
          </Section>

          <Section title="Plan Details">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <Field label="Plan Name" required error={errors.plan_name?.message}>
                <Input data-testid="plan-name-input" {...register("plan_name")} className="rounded-sm" />
              </Field>
              <Field label="Plan Type" required>
                <Select value={v.type} onValueChange={(val) => setValue("type", val, { shouldValidate: true })}>
                  <SelectTrigger data-testid="plan-type-select" className="rounded-sm"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Fixed Plan">Fixed Plan</SelectItem>
                    <SelectItem value="Offer Plan">Offer Plan</SelectItem>
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Years" required>
                <Select value={String(v.years)} onValueChange={(val) => setValue("years", Number(val), { shouldValidate: true })}>
                  <SelectTrigger data-testid="plan-years-select" className="rounded-sm"><SelectValue /></SelectTrigger>
                  <SelectContent>{[...Array(11).keys()].map((n) => <SelectItem key={n} value={String(n)}>{n}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
              <Field label="Months" required error={errors.months?.message}>
                <Select value={String(v.months)} onValueChange={(val) => setValue("months", Number(val), { shouldValidate: true })}>
                  <SelectTrigger data-testid="plan-months-select" className="rounded-sm"><SelectValue /></SelectTrigger>
                  <SelectContent>{[...Array(12).keys()].map((n) => <SelectItem key={n} value={String(n)}>{n}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
              <div className="sm:col-span-2 rounded-sm bg-slate-50 px-3 py-2 text-xs text-slate-600">
                Total duration: <span className="font-mono font-semibold text-slate-900">{totalMonths} months</span>
              </div>
              <Field label="Start Date" required error={errors.start_date?.message}>
                <Input type="date" data-testid="plan-start-input" {...register("start_date")} min={isEdit ? undefined : today} className="rounded-sm" />
              </Field>
              <Field label="End Date" required error={errors.end_date?.message}>
                <Input type="date" data-testid="plan-end-input" {...register("end_date")} min={v.start_date || today} className="rounded-sm" />
              </Field>
            </div>
          </Section>

          <Section title="Pricing & Grace">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-4">
              <Field label="Currency" required>
                <Select value={v.currency || "INR"} onValueChange={(val) => setValue("currency", val, { shouldValidate: true })}>
                  <SelectTrigger data-testid="plan-currency-select" className="rounded-sm"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Object.keys(CURRENCY_MAP).map((c) => (
                      <SelectItem key={c} value={c}>
                        <span className="font-mono">{CURRENCY_MAP[c].symbol}</span> · {c}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
              <Field label={`Base Price (${currencySymbol(v.currency || "INR")})`} required error={errors.price?.message}>
                <Input type="number" step="0.01" data-testid="plan-price-input" {...register("price")} className="rounded-sm font-mono" />
              </Field>
              <Field label="GST %" error={errors.tax_percentage?.message}>
                <Input type="number" step="0.01" data-testid="plan-tax-input" {...register("tax_percentage")} className="rounded-sm font-mono" />
              </Field>
              <Field label="Grace Period (days)" error={errors.grace_period_days?.message}>
                <Input type="number" data-testid="plan-grace-input" {...register("grace_period_days")} className="rounded-sm font-mono" />
              </Field>
              <div className="sm:col-span-4 rounded-sm border border-indigo-200 bg-indigo-50 px-3 py-2 text-sm text-[#002FA7]">
                Final Price (incl. GST): <span className="ml-2 font-mono font-semibold">{formatMoney(finalPrice, v.currency || "INR")}</span>
              </div>
            </div>
          </Section>

          <Section title="Early-bird Incentive">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
              <div className="sm:col-span-3">
                <label className="flex items-center gap-2 rounded-sm border border-slate-200 bg-white px-3 py-2 text-sm">
                  <input
                    type="checkbox"
                    {...register("early_bird_enabled")}
                    data-testid="plan-early-bird-toggle"
                  />
                  <span>Reward early renewals — apply a discount when subscribers renew well before expiry.</span>
                </label>
              </div>
              <Field label="Days before expiry">
                <Input
                  type="number"
                  data-testid="plan-early-bird-days"
                  {...register("early_bird_days_threshold")}
                  disabled={!v.early_bird_enabled}
                  className="rounded-sm font-mono"
                />
              </Field>
              <Field label="Discount %" error={errors.early_bird_discount_percent?.message}>
                <Input
                  type="number"
                  step="0.01"
                  data-testid="plan-early-bird-percent"
                  {...register("early_bird_discount_percent")}
                  disabled={!v.early_bird_enabled}
                  className="rounded-sm font-mono"
                />
              </Field>
              {v.early_bird_enabled && Number(v.early_bird_discount_percent) > 0 && (
                <div className="sm:col-span-3 rounded-sm border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs text-emerald-800">
                  Subscribers who renew <span className="font-mono font-semibold">{v.early_bird_days_threshold || 10}+ days</span> before expiry will pay
                  <span className="ml-1 font-mono font-semibold">
                    {formatMoney(finalPrice * (1 - Number(v.early_bird_discount_percent) / 100), v.currency || "INR")}
                  </span>
                  <span className="ml-1">(saves {Number(v.early_bird_discount_percent).toFixed(2)}%)</span>
                </div>
              )}
            </div>
          </Section>

          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={() => navigate("/subscription-plans")} className="rounded-sm">Cancel</Button>
            <Button type="submit" disabled={submitting} data-testid="plan-submit-btn" className="rounded-sm bg-[#002FA7] hover:bg-[#00227A]">
              <Save size={14} className="mr-1.5" />{submitting ? "Saving…" : isEdit ? "Save changes" : "Create plan"}
            </Button>
          </div>
        </form>

        <aside className="space-y-4">
          <div className="rounded-sm border border-slate-200 bg-gradient-to-br from-indigo-50 via-white to-white p-5">
            <div className="flex items-center justify-between">
              <div className="text-[11px] font-semibold uppercase tracking-[0.2em] text-[#002FA7]">{v.type}</div>
              <span className="rounded-sm border border-slate-200 bg-white px-1.5 py-0.5 text-[10px] font-mono font-semibold text-slate-700">{v.currency || "INR"}</span>
            </div>
            <div className="mt-1 text-xl font-semibold tracking-tight text-slate-900">{v.plan_name || "Untitled plan"}</div>
            <div className="mt-3 flex items-baseline gap-1">
              <span className="text-3xl font-semibold text-slate-900">{formatMoney(finalPrice, v.currency || "INR")}</span>
              <span className="text-xs text-slate-500">/ {Math.floor(totalMonths/12)}Y {totalMonths%12}M</span>
            </div>
            <div className="mt-3 space-y-1 border-t border-slate-200 pt-3 text-xs text-slate-600">
              <div className="flex justify-between"><span>Base</span><span className="font-mono">{formatMoney(Number(v.price || 0), v.currency || "INR")}</span></div>
              <div className="flex justify-between"><span>GST ({v.tax_percentage || 0}%)</span><span className="font-mono">{formatMoney((Number(v.price)||0)*(Number(v.tax_percentage)||0)/100, v.currency || "INR")}</span></div>
              <div className="flex justify-between font-semibold text-slate-900"><span>Final</span><span className="font-mono">{formatMoney(finalPrice, v.currency || "INR")}</span></div>
            </div>
            <div className="mt-3 text-xs text-slate-500">Valid: {v.start_date || "—"} → {v.end_date || "—"}{v.grace_period_days ? ` · +${v.grace_period_days}d grace` : ""}</div>
          </div>

          {isEdit && history.length > 0 && (
            <div className="rounded-sm border border-slate-200 bg-white p-4" data-testid="plan-history-panel">
              <div className="mb-3 flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.2em] text-slate-500">
                <History size={12} /> Change History
              </div>
              <ol className="space-y-4 text-xs">
                {history.slice(0, 10).map((h) => (
                  <li key={h.id} className="border-l-2 border-[#002FA7] pl-3" data-testid={`plan-history-${h.id}`}>
                    <div className="flex items-center gap-2">
                      <span className="rounded-sm bg-indigo-50 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-[#002FA7]">{h.change_reason}</span>
                      <span className="text-slate-500">{new Date(h.changed_at).toLocaleString("en-IN")}</span>
                    </div>
                    <div className="mt-0.5 font-mono text-[10px] text-slate-400">{h.changed_by_email} · {h.ip_address}</div>
                    {Array.isArray(h.diffs) && h.diffs.length > 0 && (
                      <ul className="mt-2 space-y-1">
                        {h.diffs.map((d, i) => {
                          const isCur = (v.currency || "INR");
                          const isMoneyField = d.field === "price";
                          const oldS = isMoneyField ? formatMoney(d.old, isCur) : formatDiffValue(d.old);
                          const newS = isMoneyField ? formatMoney(d.new, isCur) : formatDiffValue(d.new);
                          return (
                            <li key={i} className="rounded-sm border border-slate-200 bg-slate-50 px-2 py-1 text-[11px]">
                              <span className="font-semibold text-slate-700">{humanizeField(d.field)}: </span>
                              <span className="rounded-sm bg-rose-100 px-1 py-0.5 font-mono text-rose-700 line-through">{oldS}</span>
                              <ArrowRight size={10} className="mx-1 inline text-slate-400" />
                              <span className="rounded-sm bg-emerald-100 px-1 py-0.5 font-mono text-emerald-700">{newS}</span>
                            </li>
                          );
                        })}
                      </ul>
                    )}
                    {Array.isArray(h.diffs) && h.diffs.length === 0 && (
                      <div className="mt-1 text-[11px] italic text-slate-400">No field-level changes captured.</div>
                    )}
                  </li>
                ))}
              </ol>
            </div>
          )}
        </aside>
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div>
      <div className="mb-4 border-b border-slate-200 pb-2 text-[11px] font-semibold uppercase tracking-[0.2em] text-slate-500">{title}</div>
      {children}
    </div>
  );
}

function Field({ label, required, error, children }) {
  return (
    <div>
      <Label className="mb-1 block text-sm font-medium text-slate-700">
        {label} {required && <Required />}
      </Label>
      {children}
      {error && <p className="mt-1 text-xs text-rose-600">{error}</p>}
    </div>
  );
}
